import type { AppState } from '../types';

const DAY_MS = 86_400_000;
const DEFAULT_TIMEZONE = 'Asia/Samarkand';

export function localDateKey(date = new Date(), timeZone = DEFAULT_TIMEZONE) {
  const format = (zone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  };
  try { return format(timeZone); } catch { return format(DEFAULT_TIMEZONE); }
}

function ordinal(key: string) {
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return Number.NaN;
  return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);
}

export function calendarDistance(fromKey: string, toKey: string) {
  const a = ordinal(fromKey);
  const b = ordinal(toKey);
  return Number.isFinite(a) && Number.isFinite(b) ? b - a : 0;
}

export function courseStartKey(state: AppState) {
  const tz = state.profile.timezone || DEFAULT_TIMEZONE;
  if (/^\d{4}-\d{2}-\d{2}$/.test(state.profile.courseStartedOn ?? '')) {
    return state.profile.courseStartedOn as string;
  }
  for (const raw of [
    state.profile.onboardingCompletedAt,
    state.profile.trialStartedAt,
    state.profile.createdAt,
  ]) {
    if (raw && !Number.isNaN(Date.parse(raw))) return localDateKey(new Date(raw), tz);
  }
  return localDateKey(new Date(), tz);
}

function dateKeyFromOrdinal(value: number) {
  return new Date(value * DAY_MS).toISOString().slice(0, 10);
}

export function calendarSnapshot(state: AppState, date = new Date()) {
  const timezone = state.profile.timezone || DEFAULT_TIMEZONE;
  const startedOn = courseStartKey(state);
  const courseDate = localDateKey(date, timezone);
  const distance = Math.max(0, calendarDistance(startedOn, courseDate));
  const day = Math.max(1, Math.min(365, distance + 1));
  const startOrdinal = ordinal(startedOn);
  const todayOrdinal = ordinal(courseDate);
  const completionDates = [...new Set(state.progress.dailyCompletionDates ?? [])]
    .filter((value) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      const valueOrdinal = ordinal(value);
      return Number.isFinite(startOrdinal) && Number.isFinite(valueOrdinal)
        && valueOrdinal >= startOrdinal && valueOrdinal < startOrdinal + 365;
    })
    .sort();
  const completed = new Set(completionDates);
  const todayMissionCompleted = distance < 365 && completed.has(courseDate);

  let missedDays = 0;
  if (Number.isFinite(startOrdinal) && Number.isFinite(todayOrdinal)) {
    for (let value = startOrdinal; value < todayOrdinal && value < startOrdinal + 365; value += 1) {
      if (!completed.has(dateKeyFromOrdinal(value))) missedDays += 1;
    }
  }

  let streak = 0;
  if (Number.isFinite(todayOrdinal)) {
    let cursor = todayMissionCompleted ? todayOrdinal : todayOrdinal - 1;
    while (cursor >= startOrdinal && completed.has(dateKeyFromOrdinal(cursor))) {
      streak += 1;
      cursor -= 1;
    }
  }

  return {
    timezone,
    startedOn,
    courseDate,
    day,
    todayMissionCompleted,
    missedDays,
    streak,
    courseFinished: distance >= 365,
    elapsedDays: Math.min(365, distance + 1),
    completionDates,
  };
}

export function applyCalendarSnapshot(state: AppState, date = new Date()): AppState {
  const snapshot = calendarSnapshot(state, date);
  return {
    ...state,
    profile: {
      ...state.profile,
      courseStartedOn: state.profile.courseStartedOn ?? snapshot.startedOn,
    },
    progress: {
      ...state.progress,
      day: snapshot.day,
      courseDate: snapshot.courseDate,
      todayMissionCompleted: snapshot.todayMissionCompleted,
      missedDays: snapshot.missedDays,
      streak: snapshot.streak,
      courseFinished: snapshot.courseFinished,
      dailyCompletionDates: snapshot.completionDates,
    },
  };
}
